import { useNavigate } from '@tanstack/react-router';
import { useEffect, useState, useMemo } from 'react';
import type {
  EmployeeCreateBodyParams,
  EmployeeUpdateBodyParams,
  TCEmployee,
} from 'src/api/generated';
import { EmployeeDraftVersion } from 'src/api/generated';
import {
  useCreateEmployeeMutation,
  useUpdateEmployeeMutation,
} from '../../../../shared/hooks/api/automations/employees/employees.mutation';
import {
  useEmployeeMetadata,
  useEmployeeVersion,
} from '../../../../shared/hooks/api/automations/employees/employees';
import Loading from 'src/modules/shared/components/atoms/loading';
import { ErrorState } from 'src/modules/shared/components/atoms/states/error-state';
import { Panel } from 'src/modules/shared/components/molecules/panel';
import Button from 'src/modules/shared/components/atoms/buttons/button';
import { Route as EmployeesIndexRoute } from 'src/routes/employees/index';
import { employeesSearchSchema } from 'src/modules/automations/schemas/employees/employees.schema';
import { Text } from 'src/modules/shared/components/atoms/text';
import { EmployeeForm } from '../../organisms/employees/employee-form';
import { useUnsavedNavigationGuardState } from 'src/modules/shared/hooks/unsaved-navigation-guard';
import { UnsavedChangesModal } from 'src/modules/shared/components/molecules/modals/unsaved-changes-modal';
import { useAlert } from 'src/contexts/alert-context';
import { PageHeader } from 'src/modules/shared/components/molecules/page-header';

interface EmployeeFormPageProps {
  employeeId?: string;
  isEdit: boolean;
}

export const EmployeeFormPage: React.FC<EmployeeFormPageProps> = ({
  employeeId,
  isEdit,
}) => {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [shouldNavigateAfterSave, setShouldNavigateAfterSave] = useState(false);
  const guard = useUnsavedNavigationGuardState(false);
  const { showAlert } = useAlert();

  const {
    employee: employeeMetadata,
    loading: metadataLoading,
    error: metadataError,
  } = useEmployeeMetadata(employeeId ?? '', isEdit && !!employeeId);

  const {
    employeeVersion,
    loading: versionLoading,
    error: versionError,
  } = useEmployeeVersion(
    employeeId ?? '',
    EmployeeDraftVersion.DRAFT,
    isEdit && !!employeeId
  );

  const loading = metadataLoading || versionLoading;
  const error = metadataError ?? versionError;

  const { createEmployee } = useCreateEmployeeMutation(
    () => {
      setIsSubmitting(false);
      guard.setHasUnsavedChanges(false);
      // TODO: check when implementing i18n
      showAlert('El agente IA se creó correctamente', 'success');
      setShouldNavigateAfterSave(true);
    },
    (error) => {
      console.error('Error creating employee:', error);
      setIsSubmitting(false);
      showAlert(
        // TODO: check when implementing i18n
        'Ocurrió un error al crear. Por favor, inténtalo de nuevo.',
        'error'
      );
    }
  );

  const { updateEmployee } = useUpdateEmployeeMutation(
    () => {
      setIsSubmitting(false);
      guard.setHasUnsavedChanges(false);
      // TODO: check when implementing i18n
      showAlert('El agente IA se actualizó correctamente', 'success');
      setShouldNavigateAfterSave(true);
    },
    (error) => {
      console.error('Error updating employee:', error);
      setIsSubmitting(false);
      showAlert(
        // TODO: check when implementing i18n
        'Ocurrió un error al actualizar. Por favor, inténtalo de nuevo.',
        'error'
      );
    }
  );

  const formData = useMemo((): TCEmployee | undefined => {
    if (!isEdit) return undefined;
    if (!employeeMetadata || !employeeVersion) return undefined;

    return {
      metadata: employeeMetadata,
      draftVersion: employeeVersion,
      publishedVersion: undefined,
    };
  }, [isEdit, employeeMetadata, employeeVersion]);

  const handleSubmit = async (
    data: EmployeeCreateBodyParams | EmployeeUpdateBodyParams
  ) => {
    setIsSubmitting(true);

    try {
      if (isEdit && employeeId) {
        await updateEmployee({
          employeeId,
          data: data as EmployeeUpdateBodyParams,
        });
      } else {
        await createEmployee(data as EmployeeCreateBodyParams);
      }
    } catch (_error) {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (!shouldNavigateAfterSave || guard.hasUnsavedChanges) {
      return;
    }

    void navigate({
      to: EmployeesIndexRoute.to,
      search: (search) => employeesSearchSchema.parse(search),
    });
    setShouldNavigateAfterSave(false);
  }, [guard.hasUnsavedChanges, navigate, shouldNavigateAfterSave]);

  const handleCancel = () => {
    void navigate({
      to: EmployeesIndexRoute.to,
      search: (search) => employeesSearchSchema.parse(search),
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loading />
      </div>
    );
  }

  if (error) {
    return (
      <ErrorState
        // TODO: check when implementing i18n
        title="Error al cargar los datos del agente IA"
        description="No se pudieron cargar los datos del agente IA. Por favor, inténtalo de nuevo."
        onAction={() => {
          void navigate({
            to: EmployeesIndexRoute.to,
            search: (search) => employeesSearchSchema.parse(search),
          });
        }}
        actionContent="Volver"
      />
    );
  }

  return (
    <div className="flex flex-col h-full">
      <Panel className="h-full flex flex-col">
        <PageHeader
          title={isEdit ? 'Editar agente IA' : 'Crear agente IA'}
          onBack={handleCancel}
          backLabel="Volver a Agentes IA"
          actions={
            <div className="flex w-full flex-col-reverse gap-2 sm:w-auto sm:flex-row">
              <Button
                variant="secondary"
                appearance="outline"
                onClick={handleCancel}
                disabled={isSubmitting}
                size="md"
                className="w-full sm:w-auto"
              >
                {/* TODO: check when implementing i18n */}
                <Text size="small">Cancelar</Text>
              </Button>
              <Button
                variant="primary"
                type="submit"
                form="employee-form"
                disabled={isSubmitting}
                loading={isSubmitting}
                size="md"
                className="w-full sm:w-auto"
              >
                {/* TODO: check when implementing i18n */}
                <Text size="small">{isEdit ? 'Actualizar' : 'Crear'}</Text>
              </Button>
            </div>
          }
        />

        <div className="flex-1 overflow-auto">
          <EmployeeForm
            employee={formData}
            onSubmit={handleSubmit}
            onDirtyChange={guard.setHasUnsavedChanges}
          />
        </div>
      </Panel>

      <UnsavedChangesModal
        isOpen={guard.isBlocked}
        onProceed={guard.proceed}
        onStay={guard.stay}
      />
    </div>
  );
};
